import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authFetch } from "../../App";
import { ArrowLeft, Zap, Wrench, Eye, ChevronRight, Loader2, ClipboardCheck, Calculator } from "lucide-react";

const API = "/api";

const TOOL_ICONS = {
  "traffic-light": Zap,
  wrench: Wrench,
  eye: Eye,
};

const STATUS_COLORS = {
  aktiv: { bg: "bg-emerald-500/15", text: "text-emerald-400", dot: "bg-emerald-400" },
  inaktiv: { bg: "bg-gray-500/15", text: "text-gray-400", dot: "bg-gray-500" },
  wartung: { bg: "bg-amber-500/15", text: "text-amber-400", dot: "bg-amber-400" },
};

const TYPE_LABELS = {
  simulation: "Simulation",
  schaltplan: "Schaltplan",
  info: "Information",
};

export default function ProjektTools() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [tools, setTools] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [toolsRes, projRes] = await Promise.all([
          authFetch(`${API}/projects/${projectId}/tools`),
          authFetch(`${API}/projects/${projectId}`),
        ]);
        if (toolsRes.ok) setTools(await toolsRes.json());
        if (projRes.ok) setProject(await projRes.json());
      } catch (e) {
        console.error("Load error:", e);
      }
      setLoading(false);
    };
    load();
  }, [projectId]);

  const getToolRoute = (tool) => {
    if (tool.name === "Torampel Steuerung") {
      const base = window.location.pathname.includes("/app/m/")
        ? `/app/m/projekt-tools/${projectId}/torampel`
        : `/app/projekte/${projectId}/tools/torampel`;
      return base;
    }
    return null;
  };

  const BUILTIN_TOOLS = [
    {
      id: "kabelrechner",
      name: "Kabelrechner",
      description: "Querschnitt, Spannungsfall und Sicherung berechnen",
      icon: "calculator",
      route: window.location.pathname.includes("/app/m/")
        ? `/app/m/projekt-tools/${projectId}/kabelrechner`
        : `/app/projekte/${projectId}/tools/kabelrechner`,
    },
    {
      id: "pruefprotokolle",
      name: "Pruefprotokolle",
      description: "Erstpruefung, wiederkehrende Pruefung, Abnahme",
      icon: "clipboard",
      route: window.location.pathname.includes("/app/m/")
        ? `/app/m/projekt-tools/${projectId}/pruefprotokolle`
        : `/app/projekte/${projectId}/tools/pruefprotokolle`,
    },
  ];

  const BUILTIN_ICONS = {
    calculator: Calculator,
    clipboard: ClipboardCheck,
  };

  const handleToolClick = (tool) => {
    const route = getToolRoute(tool);
    if (route) navigate(route);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-gray-500 animate-spin" />
      </div>
    );
  }

  const projectName = project?.display_name || project?.name || "Projekt";

  return (
    <div className="min-h-screen bg-[#0f1117] text-white" data-testid="projekt-tools-page">
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-400 active:text-white transition-colors mb-3"
          data-testid="tools-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurueck
        </button>
        <h1 className="text-xl font-bold text-white" data-testid="tools-title">Projekt Tools</h1>
        <p className="text-sm text-gray-500 mt-0.5" data-testid="tools-project-name">{projectName}</p>

        {/* Nina Projekt-Chat direkt öffnen (mit Projekt-Kontext) */}
        <button
          onClick={() => navigate(`/app/nina?project=${projectId}&project_name=${encodeURIComponent(projectName)}`)}
          className="mt-3 w-full bg-gradient-to-r from-indigo-500/20 to-fuchsia-500/15 border border-indigo-400/30 rounded-xl p-3 flex items-center gap-3 active:opacity-80 transition-opacity text-left"
          data-testid="tools-open-nina-btn"
        >
          <div className="w-9 h-9 rounded-lg bg-indigo-500/25 flex items-center justify-center flex-shrink-0">
            <span className="text-[15px] font-bold text-indigo-200">N</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-white">Nina zum Projekt öffnen</div>
            <div className="text-[11px] text-gray-400 truncate">Chat kennt {projectName} automatisch</div>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Built-in Tools */}
      <div className="px-4 pt-4 pb-2 space-y-3" data-testid="builtin-tools">
        {BUILTIN_TOOLS.map((tool) => {
          const Icon = BUILTIN_ICONS[tool.icon] || Wrench;
          return (
            <button
              key={tool.id}
              onClick={() => navigate(tool.route)}
              className="w-full bg-white/[0.04] border border-white/8 rounded-2xl p-5 flex items-center gap-4 active:bg-white/[0.08] transition-all text-left group"
              data-testid={`builtin-tool-${tool.id}`}
            >
              <div className="w-14 h-14 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Icon className="w-7 h-7 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[15px] font-semibold text-white truncate">{tool.name}</h3>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{tool.description}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-600 group-active:text-white flex-shrink-0" />
            </button>
          );
        })}
      </div>

      {/* Dynamic Tool Cards */}
      <div className="px-4 pt-2 pb-8 space-y-3" data-testid="tools-list">
        {tools.length === 0 ? (
          <div className="text-center py-12">
            <Wrench className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Keine Tools fuer dieses Projekt</p>
          </div>
        ) : (
          tools.map((tool) => {
            const Icon = TOOL_ICONS[tool.icon] || Wrench;
            const sc = STATUS_COLORS[tool.status] || STATUS_COLORS.inaktiv;
            const typeLabel = TYPE_LABELS[tool.type] || tool.type;

            return (
              <button
                key={tool.id}
                onClick={() => handleToolClick(tool)}
                className="w-full bg-white/[0.04] border border-white/8 rounded-2xl p-5 flex items-center gap-4 active:bg-white/[0.08] transition-all text-left group"
                data-testid={`tool-card-${tool.id}`}
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-7 h-7 text-blue-400" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-[15px] font-semibold text-white truncate">{tool.name}</h3>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{tool.description}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${sc.bg} ${sc.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                      {tool.status}
                    </span>
                    <span className="text-[10px] text-gray-600 uppercase tracking-wider font-medium">{typeLabel}</span>
                  </div>
                </div>

                {/* Arrow */}
                <ChevronRight className="w-5 h-5 text-gray-600 group-active:text-white flex-shrink-0 transition-colors" />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
